import { useCallback, useId, useRef, useState } from "react";
import clsx from "clsx";
import { Check, ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";
import { AcDialog, Field, Icon, Sheet, useMenuDismiss } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import {
  useAdvanceLooks, useCreateAdvanceLook, useDeleteAdvanceLook, useUpdateAdvanceLook,
  type LookId,
} from "../../app/hooks/useProduction.js";
import styles from "./AdvanceLookSwitcher.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/** The looks bands start from (spec §2.4's `+ Add look…` menu). Custom opens a name dialog. */
const LOOK_PRESETS = ["Fly Date", "Festival", "Acoustic", "Solo"] as const;

/**
 * ADVANCE LOOKS (spec §2.4) as ONE control in the page header's right slot (PO, ART-251 2026-10-02 — the pill row
 * in its own card was "a bad layout for mobile altogether"). A look is a variant of the tech pack with its OWN
 * venue link, so a festival never sees the club advance — and because it switches EVERYTHING below it, it belongs
 * at the top of the page as a mode, not as a card in the middle of the content.
 *
 * The trigger reads `Look: Regular ▾`. Two containers, one list:
 *  - **Mobile** — a bottom Sheet of looks to switch between. NO create / rename / delete: mobile Advance Docs
 *    consumes and desktop creates (the nav document's own rule), so a phone gets the switch and nothing else.
 *  - **Desktop** — a dropdown under the trigger with the same list, plus (for a writer) Rename / Delete per look
 *    and a "New look" section of the presets + Custom.
 *
 * `Regular` is the FIRST row and is not a look: it is the absence of one (`lookId === null`), the shape the API
 * uses, so "delete Regular" is not expressible. A band with no looks has only Regular — the control renders
 * NOTHING then, unless it is a desktop writer, for whom it is how the first look gets made.
 *
 * `onSelect` is the CALLER's guarded switch (the editor prompts before discarding an unsaved draft).
 */
export function AdvanceLookSwitcher({ lookId, onSelect, canWrite }: {
  lookId: LookId;
  onSelect: (lookId: LookId) => void;
  canWrite: boolean;
}) {
  const isDesktop = !usePhoneLayout();
  const canManage = canWrite && isDesktop;
  const q = useAdvanceLooks();
  const create = useCreateAdvanceLook();
  const rename = useUpdateAdvanceLook();
  const remove = useDeleteAdvanceLook();

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState<{ mode: "create" | "rename"; lookId?: string; value: string } | null>(null);
  const [deleting, setDeleting] = useState<{ lookId: string; name: string } | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useMenuDismiss(open && isDesktop, close, rootRef, triggerRef);

  // The generated schema marks every field optional; a row without an id is unaddressable, so it is dropped.
  const looks = (q.data?.items ?? [])
    .filter((l): l is typeof l & { lookId: string } => typeof l.lookId === "string")
    .map((l) => ({ lookId: l.lookId, name: l.name ?? "Look" }));
  const remaining = q.data?.remainingSlots ?? 0;

  if (looks.length === 0 && !canManage) return null;

  const current = lookId === null ? "Regular" : looks.find((l) => l.lookId === lookId)?.name ?? "Regular";
  const pick = (to: LookId) => { setOpen(false); onSelect(to); };

  const doCreate = (name: string) =>
    create.mutate({ name }, {
      // Land ON the new look — it opens as a copy of Regular, so the band is looking at the thing it will edit.
      onSuccess: (look) => {
        setNaming(null);
        if (look.lookId) onSelect(look.lookId);
        pushToast(`"${look.name ?? "Look"}" created.`, "success");
      },
      onError: (e) => pushToast(errorMessage(e, "Couldn't create the look."), "error"),
    });

  const doRename = (id: string, name: string) =>
    rename.mutate({ lookId: id, name }, {
      onSuccess: (look) => { setNaming(null); pushToast(`Renamed to "${look.name ?? "Look"}".`, "success"); },
      onError: (e) => pushToast(errorMessage(e, "Couldn't rename the look."), "error"),
    });

  const doDelete = (id: string) =>
    remove.mutate({ lookId: id }, {
      // Back to Regular if the open look went — staying on it would leave the editor asking for rows the
      // server has just dropped.
      onSuccess: () => {
        setDeleting(null);
        if (lookId === id) onSelect(null);
        pushToast("Look deleted — its venue link no longer works.", "success");
      },
      onError: (e) => pushToast(errorMessage(e, "Couldn't delete the look."), "error"),
    });

  // A preset already used is dropped — offering it again could only 422 on the duplicate name.
  const presets = LOOK_PRESETS.filter((p) => !looks.some((l) => l.name.toLowerCase() === p.toLowerCase()));

  const rows = (
    <>
      <LookRow name="Regular" selected={lookId === null} onPick={() => pick(null)} />
      {looks.map((l) => (
        <LookRow key={l.lookId} name={l.name} selected={lookId === l.lookId} onPick={() => pick(l.lookId)}
          onRename={canManage ? () => { setOpen(false); setNaming({ mode: "rename", lookId: l.lookId, value: l.name }); } : undefined}
          onDelete={canManage ? () => { setOpen(false); setDeleting(l); } : undefined} />
      ))}
    </>
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <button ref={triggerRef} type="button" className={styles.trigger}
        aria-haspopup={isDesktop ? "menu" : "dialog"} aria-expanded={open} aria-controls={isDesktop ? menuId : undefined}
        aria-label={`Advance look: ${current}`} onClick={() => setOpen((o) => !o)}>
        <span className={styles.triggerKey}>Look</span>
        <span className={styles.triggerValue}>{current}</span>
        <Icon icon={ChevronDown} size={16} />
      </button>

      {open && isDesktop && (
        <div className={styles.menu} role="menu" id={menuId} aria-label="Advance looks">
          {rows}
          {canManage && remaining > 0 && (
            <>
              <div className={styles.sectionLabel} role="presentation">New look</div>
              {presets.map((p) => (
                <button key={p} type="button" role="menuitem" className={styles.addItem}
                  disabled={create.isPending} onClick={() => { setOpen(false); doCreate(p); }}>
                  <Icon icon={Plus} size={16} />{p}
                </button>
              ))}
              <button type="button" role="menuitem" className={styles.addItem}
                onClick={() => { setOpen(false); setNaming({ mode: "create", value: "" }); }}>
                <Icon icon={Plus} size={16} />Custom name…
              </button>
            </>
          )}
        </div>
      )}

      {open && !isDesktop && (
        <Sheet title="Advance looks" onClose={close}>
          <div className={styles.sheet}>
            <p className={styles.sheetTitle}>Advance looks</p>
            <div className={styles.sheetList}>{rows}</div>
          </div>
        </Sheet>
      )}

      {naming && (
        <AcDialog
          title={naming.mode === "create" ? "Name this look" : "Rename look"}
          confirmLabel={naming.mode === "create" ? "Create" : "Rename"}
          loading={create.isPending || rename.isPending}
          onCancel={() => setNaming(null)}
          onConfirm={() => {
            const name = naming.value.trim();
            if (!name) return;
            if (naming.mode === "create") doCreate(name);
            else if (naming.lookId) doRename(naming.lookId, name);
          }}
        >
          <Field label="Look name" value={naming.value} placeholder="e.g. Trio, Stripped"
            onChange={(e) => setNaming({ ...naming, value: e.target.value })} />
          {naming.mode === "create" && (
            <p className={styles.hint}>It starts as a copy of your Regular advance, so you can tweak rather than re-enter.</p>
          )}
        </AcDialog>
      )}

      {deleting && (
        <AcDialog
          title={`Delete "${deleting.name}"?`}
          // Both confirms say the link dies (spec): what a band cannot get back is the URL venues already hold.
          message="Its venue link stops working immediately — anyone holding that URL will get a 404. Your Regular advance is untouched."
          confirmLabel="Delete look"
          tone="danger"
          loading={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => doDelete(deleting.lookId)}
        />
      )}
    </div>
  );
}

/** One look in the list: the row itself selects; a writer on desktop also gets Rename / Delete on the row. */
function LookRow({ name, selected, onPick, onRename, onDelete }: {
  name: string;
  selected: boolean;
  onPick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className={clsx(styles.row, selected && styles.rowOn)}>
      <button type="button" role="menuitem" className={styles.rowPick} aria-current={selected || undefined} onClick={onPick}>
        <span className={styles.rowCheck} aria-hidden="true">{selected && <Icon icon={Check} size={16} />}</span>
        <span className={styles.rowName}>{name}</span>
      </button>
      {onRename && (
        <button type="button" className={styles.rowAction} aria-label={`Rename ${name}`} onClick={onRename}>
          <Icon icon={Pencil} size={16} />
        </button>
      )}
      {onDelete && (
        <button type="button" className={clsx(styles.rowAction, styles.rowDanger)} aria-label={`Delete ${name}`} onClick={onDelete}>
          <Icon icon={Trash2} size={16} />
        </button>
      )}
    </div>
  );
}
